<template>
    <div>
        <div
            :class="[
                hasError(errors, 'payment_method_id')
                    ? 'border-red-600 bg-white'
                    : blacklisted
                      ? 'border-black bg-gray-800'
                      : 'border-gray-200 bg-white',
                'overflow-hidden shadow rounded-lg border',
            ]"
        >
            <div
                :class="[
                    hasError(errors, 'payment_method_id')
                        ? 'bg-red-500 text-white border-red-600'
                        : blacklisted
                          ? 'bg-gray-900 text-white border-black'
                          : 'bg-gray-100 text-gray-800 border-gray-200',
                    'flex items-center px-4 py-5 sm:px-6 border-b',
                ]"
            >
                <CreditCardIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                <h2 class="font-bold">Payment Method</h2>
            </div>

            <div class="px-4 py-3 sm:px-6">
                <div
                    v-if="paymentType !== 'card'"
                    class="py-3 flex text-gray-700"
                >
                    <CreditCardIcon
                        class="h-5 w-5 mr-2 shrink-0"
                        aria-hidden="true"
                    />

                    <div class="flex items-center">
                        <div class="mr-3">
                            <PaymentMethodIcon
                                :alt="ucwords(paymentType)"
                                :brand="paymentType"
                                class="w-20 h-auto shadow rounded-md"
                            />
                        </div>
                    </div>
                </div>

                <div v-else-if="paymentMethod">
                    <div
                        :class="[
                            blacklisted ? 'text-white' : 'text-gray-700',
                            'py-3 flex',
                        ]"
                    >
                        <UserIcon
                            class="h-5 w-5 mr-2 shrink-0"
                            aria-hidden="true"
                        />

                        <div>
                            <p>
                                {{ paymentMethod.address.name }}
                                <span v-if="blacklisted" class="font-semibold"
                                    >(Blacklisted)</span
                                >
                            </p>
                        </div>
                    </div>

                    <div
                        :class="[
                            blacklisted ? 'text-white' : 'text-gray-700',
                            'py-3 flex',
                        ]"
                    >
                        <CreditCardIcon
                            class="h-5 w-5 mr-2 shrink-0"
                            aria-hidden="true"
                        />

                        <div class="flex items-center">
                            <div class="mr-3">
                                <PaymentMethodIcon
                                    :alt="cardTypeName"
                                    :brand="cardTypeSlug"
                                    class="w-20 h-auto shadow rounded-md"
                                />
                            </div>

                            <div>
                                <p>Ends in {{ last4 }}</p>

                                <p class="flex items-center">
                                    Expires
                                    <UpdateUserCheckoutPaymentMethodExpirationButton
                                        class="ml-1"
                                        :csrf="csrf"
                                        :errors="errors"
                                        :old="old"
                                        :paymentMethod="paymentMethod"
                                    />
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        :class="[
                            blacklisted ? 'text-white' : 'text-gray-700',
                            'py-3 flex',
                        ]"
                    >
                        <LocationMarkerIcon
                            class="h-5 w-5 mr-2 shrink-0"
                            aria-hidden="true"
                        />

                        <div>
                            <p>
                                {{ paymentMethod.address.line1 }}
                                <span v-if="paymentMethod.address.line2">
                                    <br />
                                    {{ paymentMethod.address.line2 }}
                                </span>
                            </p>

                            <p>
                                <span v-if="paymentMethod.address.state">
                                    {{ paymentMethod.address.city }},
                                    {{ paymentMethod.address.state }}
                                    {{ paymentMethod.address.postal_code }}
                                </span>

                                <span v-else>
                                    {{ paymentMethod.address.city }}
                                    {{ paymentMethod.address.postal_code }}
                                </span>
                            </p>

                            <p>
                                {{ paymentMethod.address.country.name }}
                            </p>
                        </div>
                    </div>
                </div>

                <div v-else class="py-3 flex justify-center text-gray-700">
                    <div>
                        <p
                            :class="[
                                hasError(errors, 'payment_method_id')
                                    ? 'text-red-900'
                                    : 'text-gray-700',
                                'text-center',
                            ]"
                        >
                            {{
                                hasError(errors, "payment_method_id")
                                    ? error(errors, "payment_method_id")
                                    : "You haven't added any payment methods yet."
                            }}
                        </p>
                    </div>
                </div>

                <ChangeUserCheckoutPaymentMethodButton
                    :addresses="addresses"
                    :canPlaceOfflineOrders="canPlaceOfflineOrders"
                    :countries="countries"
                    :csrf="csrf"
                    :emailOptIn="emailOptIn"
                    :errors="errors"
                    :includeVerifyField="includeVerifyField"
                    :initialPaymentMethod="paymentMethod"
                    :initialCountry="country"
                    :initialStates="states"
                    :old="old"
                    :paymentMethods="paymentMethods"
                    :paymentType="paymentType"
                    :paymentGateway="paymentGateway"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
    CreditCardIcon,
    LocationMarkerIcon,
    UserIcon,
} from "@heroicons/vue/solid";
import PaymentMethodIcon from "../paymentMethods/PaymentMethodIcon.vue";
import ChangeUserCheckoutPaymentMethodButton from "./ChangeUserCheckoutPaymentMethodButton.vue";
import UpdateUserCheckoutPaymentMethodExpirationButton from "./UpdateUserCheckoutPaymentMethodExpirationButton.vue";
import { useForm } from "../../functions/useForm";
import { useString } from "../../functions/useString";

const props = withDefaults(
    defineProps<{
        addresses?: any;
        canPlaceOfflineOrders?: boolean;
        canSeeIfAnyAddressIsBlacklisted?: boolean;
        countries?: any;
        country?: any;
        csrf?: string;
        emailOptIn?: string;
        errors?: any;
        includeVerifyField?: boolean;
        initialCountry?: any;
        old?: any;
        paymentGateway?: any;
        paymentMethod?: any;
        paymentMethods?: any;
        paymentType?: string;
        states?: any;
    }>(),
    {
        canPlaceOfflineOrders: false,
        canSeeIfAnyAddressIsBlacklisted: false,
        emailOptIn: "1",
        paymentType: "card",
    },
);

const { error, hasError } = useForm();
const { ucwords } = useString();

const blacklisted = computed(
    () =>
        props.canSeeIfAnyAddressIsBlacklisted &&
        props.paymentMethod &&
        props.paymentMethod.address &&
        props.paymentMethod.address.blacklisted,
);
const cardTypeName = computed(() =>
    _.startCase(props.paymentMethod.card_type.toLowerCase().replace(/_/g, " ")),
);
const cardTypeSlug = computed(() =>
    props.paymentMethod.card_type.toLowerCase().replace(/_/g, "-"),
);
const last4 = computed(() => props.paymentMethod.card_number.replace(/X/g, ""));
</script>
